import { REALTIME_MAX_FRAME_BYTES } from "./realtimeEvents";

/** One complete server-sent-events frame: the `event:` name and the joined `data:` lines. */
export interface SseFrame {
  event: string;
  data: string;
}

const encoder = new TextEncoder();

/**
 * Incremental SSE frame parser (PWA-2689). Feed raw body chunks in order and
 * collect the complete frames. Handles frames split across chunks, `\r\n`
 * line endings, multi-line `data:`, and ignores `id:`, `retry:` and comment
 * lines (the gateway sends none, but the format allows them). Frames without
 * `data:` are dropped. A frame or an unterminated buffer larger than the
 * server's 8 KiB bound is dropped so a malformed stream cannot grow memory.
 */
export class SseFrameParser {
  private readonly decoder = new TextDecoder("utf-8");
  private buffer = "";
  /**
   * A chunk that ends in CR cannot be normalized yet: if the next chunk starts
   * with LF the pair is one CRLF, not a CR line end followed by an LF blank line.
   * The CR is held back and prepended to the next chunk before normalization.
   */
  private pendingCr = "";

  push(chunk: Uint8Array): SseFrame[] {
    let text = this.pendingCr + this.decoder.decode(chunk, { stream: true });
    this.pendingCr = "";
    if (text.endsWith("\r")) {
      this.pendingCr = "\r";
      text = text.slice(0, -1);
    }
    this.buffer += text.replace(/\r\n?/g, "\n");
    const frames: SseFrame[] = [];
    let boundary = this.buffer.indexOf("\n\n");
    while (boundary !== -1) {
      const block = this.buffer.slice(0, boundary);
      this.buffer = this.buffer.slice(boundary + 2);
      const frame = parseBlock(block);
      if (frame) frames.push(frame);
      boundary = this.buffer.indexOf("\n\n");
    }
    if (encoder.encode(this.buffer).length > REALTIME_MAX_FRAME_BYTES) {
      this.buffer = "";
    }
    return frames;
  }

  reset(): void {
    this.buffer = "";
    this.pendingCr = "";
  }
}

function parseBlock(block: string): SseFrame | null {
  if (encoder.encode(block).length > REALTIME_MAX_FRAME_BYTES) return null;
  let event = "message";
  const data: string[] = [];
  for (const rawLine of block.split("\n")) {
    if (rawLine === "" || rawLine.startsWith(":")) continue;
    const colon = rawLine.indexOf(":");
    const field = colon === -1 ? rawLine : rawLine.slice(0, colon);
    let value = colon === -1 ? "" : rawLine.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") event = value;
    else if (field === "data") data.push(value);
    // id, retry and unknown fields are ignored on purpose.
  }
  if (data.length === 0) return null;
  return { event, data: data.join("\n") };
}
